import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalizationContext } from '@im/react-localization';
import { useLocation } from 'react-router';
import { UjetTO, useUjetAuth } from '../../hooks/useUjetAuth';
import { useIsUjetChatbotEnabled } from '../../hooks/useIsUjetChatbotEnabled';
import { injectScriptInHead } from './utils';
import { useDecodedToken } from 'mf_core/auth';
import { useSiteConfig } from 'mf_core/config';
import { useSearchParams } from 'react-router-dom';

const baseUrl = globalThis.location.origin;

/** CCAI Ujet Chatbot/Live chat */
export function useUjetUniteChatBot() {
  const { locale, localizationService } = useLocalizationContext();
  const { environment, siteCode } = useSiteConfig();
  const langRef = useRef('');
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const openCloudChat =
    typeof searchParams.get('openCloudChat') === 'string'
      ? Boolean(searchParams.get('openCloudChat'))
      : false;
  const [ujetTranslations, setUjetTranslations] = useState<{
    [key: string]: { [key: string]: string };
  }>({});

  const ujetTranslationsRef = useRef(ujetTranslations);

  const decodedToken = useDecodedToken();
  const firstName: string = decodedToken?.imoProfile?.firstName || '';
  const lastName: string = decodedToken?.imoProfile?.lastName || '';
  const bcn: string = decodedToken?.resellerId || '';
  const email: string = decodedToken?.email || '';
  const userId: string = decodedToken?.userId || '';

  const {
    isEnabled,
    isUniteChatbot,
    isValidPath,
    translationKeys: ujetKeys,
  } = useIsUjetChatbotEnabled(true);
  const { data: ujetConfig } = useUjetAuth(isUniteChatbot, true);

  const UJET = window.UJET;
  const ujetUniteInstanceRef = useRef(null as UJETInstance);

  const [isScriptInjected, setIsScriptInjected] = useState(false);

  function hideUjet() {
    const ujet = ujetUniteInstanceRef.current;
    if (ujet) {
      ujet.$el.style.display = 'none';
    }
  }

  function showUjet() {
    const ujet = ujetUniteInstanceRef.current;
    if (ujet) {
      ujet.$el.style.display = 'block';
    }
  }

  useEffect(() => {
    langRef.current = locale.split('-')[0];
  }, [locale]);

  useEffect(() => {
    if (isEnabled) {
      if (!isScriptInjected && isUniteChatbot) {
        injectScriptInHead('https://websdk.ujet.co/v2/loader.js', () => {
          setIsScriptInjected(true);
        });
      }
      //Close chatbot widget and hide chatbot launcher button when leaving Returns routes
      else if (isScriptInjected && !isUniteChatbot) {
        hideUjet();
      }
      //Once injected, display chatbot launcher button when returning to a Returns route
      else if (isScriptInjected && isUniteChatbot) {
        showUjet();
      }
    }
  }, [
    isEnabled,
    isScriptInjected,
    isValidPath,
    pathname,
    isUniteChatbot,
    openCloudChat,
  ]);

  const keys = useMemo(() => {
    return ujetKeys.map((key) => {
      return `UjetUnite.${key}`;
    });
  }, [ujetKeys]);

  const loadTranslations = useCallback(() => {
    if (localizationService && keys.length > 0) {
      localizationService
        .translate(keys, { returnKeyIfNotFound: false })
        .then((res: any) => {
          const langTranslations: { [key: string]: string } = {};
          ujetKeys.forEach((key) => {
            langTranslations[key] = res[`UjetUnite.${key}`] || '';
          });
          ujetTranslationsRef.current = {
            ...ujetTranslations,
            [langRef.current]: langTranslations,
          };
          setUjetTranslations(ujetTranslationsRef.current);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keys, localizationService, ujetKeys]);

  function setupUjet(config: UjetTO) {
    // Initialize UJET web-sdk. For more info:
    // https://cloud.google.com/contact-center/ccai-platform/docs/API/publication--en

    const options: { [option: string]: any } = {
      authenticate: config.token,
      bottom: '20px',
      companyId: config.configurations.companyId,
      companyName: config.configurations.companyName,
      customData: {
        bcn: {
          label: 'bcn',
          value: bcn,
        },
        country: {
          label: 'country',
          value: siteCode,
        },
        email: {
          label: 'Email',
          value: email,
        },
        name: {
          label: 'name',
          value: `${firstName} ${lastName}`,
        },
        resellerid: {
          label: 'resellerid',
          value: bcn,
        },
        values: {
          label: 'values',
          value: {
            environment,
            language: locale,
            userId,
          },
        },
      },
      host: config.configurations.host,
      lang: langRef.current,
      launcher: false,
      logo: `${baseUrl}/cep/assets/img/chatbot/logo.png`,
    };
    if (Object.keys(ujetTranslationsRef.current).length > 0) {
      options.translation = ujetTranslationsRef.current;
    }
    if (config.configurations.defaultMenuKey) {
      options.menuKey = config.configurations.defaultMenuKey;
      setTimeout(() => {
        ujetUniteInstanceRef.current = new UJET(options);
        if (ujetUniteInstanceRef.current) {
          ujetUniteInstanceRef.current.on('close', () => {
            destroyUniteUjet();
            setupUjet(config);
          });
        }
      }, 100);
    } else {
      ujetUniteInstanceRef.current = new UJET(options);
    }
  }

  function destroyUniteUjet() {
    if (ujetUniteInstanceRef.current) {
      ujetUniteInstanceRef.current.destroy();
      ujetUniteInstanceRef.current.$el.remove();
    }
  }
  window.openUjet = () => {
    if (isUniteChatbot) {
      ujetUniteInstanceRef.current?.open();
    }
  };
  useEffect(() => {
    if (
      UJET &&
      ujetConfig &&
      isScriptInjected &&
      !ujetUniteInstanceRef.current
    ) {
      if (ujetKeys.length > 0 && Object.keys(ujetTranslations).length === 0) {
        // load translations before initializing Ujet.
        loadTranslations();
        return;
      }
      setupUjet(ujetConfig);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScriptInjected, ujetTranslations, ujetConfig, UJET]);

  useEffect(() => {
    if (isEnabled && ujetUniteInstanceRef.current && ujetKeys.length > 0) {
      // load translations on language change.
      if (!Object.keys(ujetTranslations).includes(langRef.current)) {
        loadTranslations();
      } else {
        ujetUniteInstanceRef.current.options.lang = langRef.current;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, locale, ujetKeys]);

  useEffect(() => {
    if (isEnabled && ujetUniteInstanceRef.current) {
      // update language and translations on language change.
      ujetUniteInstanceRef.current.options.lang = langRef.current;
      ujetUniteInstanceRef.current.options.translation = ujetTranslations;
    }
  }, [isEnabled, ujetTranslations, ujetUniteInstanceRef, openCloudChat]);

  useEffect(() => {
    if (openCloudChat) {
      setTimeout(() => {
        ujetUniteInstanceRef.current?.open();
      }, 500);
    }
  });
}

declare global {
  interface Window {
    UJET: any;
    openUjet: () => void;
  }
}

type UJETInstance = null | {
  $el: HTMLElement;
  close: () => void;
  destroy: () => void;
  on: (event: string, cb: () => void) => void;
  open: () => void;
  options: ConfigureOptions;
  start: (options?: StartOptions) => void;
  status: string;
};

type StartOptions = { [menuKey: string]: string };
type ConfigureOptions = { [key: string]: any };
